export type RegionKey = 'BR' | 'SE' | 'S' | 'NE' | 'CO' | 'N'

export interface RegionConfig {
  name: string
  center: [number, number]
  zoom: number
}

export const REGIONS: Record<RegionKey, RegionConfig> = {
  BR: { name: 'Brasil', center: [-54, -15], zoom: 1 },
  SE: { name: 'Sudeste', center: [-45, -20.5], zoom: 2.6 },
  S: { name: 'Sul', center: [-51.5, -27.5], zoom: 2.8 },
  NE: { name: 'Nordeste', center: [-41.5, -9.5], zoom: 2.2 },
  CO: { name: 'Centro-Oeste', center: [-54.5, -16], zoom: 2.1 },
  N: { name: 'Norte', center: [-61, -4.5], zoom: 1.8 },
}

export interface StateMetadata {
  name: string
  region: string
  regionKey: RegionKey
}

export const UF_METADATA: Record<string, StateMetadata> = {
  // Sudeste
  SP: { name: 'São Paulo', region: 'Sudeste', regionKey: 'SE' },
  RJ: { name: 'Rio de Janeiro', region: 'Sudeste', regionKey: 'SE' },
  MG: { name: 'Minas Gerais', region: 'Sudeste', regionKey: 'SE' },
  ES: { name: 'Espírito Santo', region: 'Sudeste', regionKey: 'SE' },

  // Sul
  PR: { name: 'Paraná', region: 'Sul', regionKey: 'S' },
  SC: { name: 'Santa Catarina', region: 'Sul', regionKey: 'S' },
  RS: { name: 'Rio Grande do Sul', region: 'Sul', regionKey: 'S' },

  // Nordeste
  BA: { name: 'Bahia', region: 'Nordeste', regionKey: 'NE' },
  PE: { name: 'Pernambuco', region: 'Nordeste', regionKey: 'NE' },
  CE: { name: 'Ceará', region: 'Nordeste', regionKey: 'NE' },
  RN: { name: 'Rio Grande do Norte', region: 'Nordeste', regionKey: 'NE' },
  PB: { name: 'Paraíba', region: 'Nordeste', regionKey: 'NE' },
  AL: { name: 'Alagoas', region: 'Nordeste', regionKey: 'NE' },
  SE: { name: 'Sergipe', region: 'Nordeste', regionKey: 'NE' },
  MA: { name: 'Maranhão', region: 'Nordeste', regionKey: 'NE' },
  PI: { name: 'Piauí', region: 'Nordeste', regionKey: 'NE' },

  // Centro-Oeste
  GO: { name: 'Goiás', region: 'Centro-Oeste', regionKey: 'CO' },
  MT: { name: 'Mato Grosso', region: 'Centro-Oeste', regionKey: 'CO' },
  MS: { name: 'Mato Grosso do Sul', region: 'Centro-Oeste', regionKey: 'CO' },
  DF: { name: 'Distrito Federal', region: 'Centro-Oeste', regionKey: 'CO' },

  // Norte
  AM: { name: 'Amazonas', region: 'Norte', regionKey: 'N' },
  PA: { name: 'Pará', region: 'Norte', regionKey: 'N' },
  RO: { name: 'Rondônia', region: 'Norte', regionKey: 'N' },
  AC: { name: 'Acre', region: 'Norte', regionKey: 'N' },
  RR: { name: 'Roraima', region: 'Norte', regionKey: 'N' },
  AP: { name: 'Amapá', region: 'Norte', regionKey: 'N' },
  TO: { name: 'Tocantins', region: 'Norte', regionKey: 'N' },
}

// Coordenadas calibradas dos centróides dos 27 estados para os marcadores
export const UF_COORDINATES: Record<string, [number, number]> = {
  // Sudeste
  SP: [-48.5, -22.2],
  RJ: [-42.5, -22.3],
  MG: [-44.5, -18.5],
  ES: [-40.3, -19.5],

  // Sul
  PR: [-51.3, -24.8],
  SC: [-50.5, -27.2],
  RS: [-52.8, -29.8],

  // Nordeste
  BA: [-41.5, -12.5],
  PE: [-37.5, -8.3],
  CE: [-39.5, -5.2],
  RN: [-36.5, -5.8],
  PB: [-36.8, -7.1],
  AL: [-36.6, -9.6],
  SE: [-37.4, -10.6],
  MA: [-45.2, -5.2],
  PI: [-42.8, -7.5],

  // Centro-Oeste
  GO: [-49.8, -15.9],
  MT: [-55.8, -13.0],
  MS: [-54.8, -20.5],
  DF: [-47.88, -15.78],

  // Norte
  AM: [-64.5, -4.2],
  PA: [-52.5, -4.0],
  RO: [-62.8, -11.0],
  AC: [-70.5, -9.2],
  RR: [-61.3, 2.0],
  AP: [-51.8, 1.4],
  TO: [-48.3, -10.2],
}

// População oficial do Brasil por estado (Estimativas Oficiais / Portarias do IBGE)
export const UF_POPULATION_IBGE: Record<string, number> = {
  SP: 46179008,
  MG: 21460311,
  RJ: 17225410,
  BA: 14889472,
  PR: 11952456,
  RS: 11233317,
  PE: 9583176,
  CE: 9302211,
  PA: 8756324,
  SC: 8312759,
  GO: 7495033,
  MA: 7024557,
  PB: 4182828,
  ES: 4150692,
  AM: 4360926,
  MT: 3950330,
  RN: 3463737,
  PI: 3392617,
  AL: 3221128,
  DF: 3009996,
  MS: 2946317,
  SE: 2307255,
  RO: 1757338,
  TO: 1595994,
  AC: 887794,
  AP: 809953,
  RR: 761012,
}

/**
 * Retorna o potencial demográfico de PDVs pelo IBGE (1 PDV a cada 1.000 habitantes)
 */
export const getUfPotencialIbge = (uf: string): number => {
  const pop = UF_POPULATION_IBGE[uf.toUpperCase()] || 0
  return Math.round(pop / 1000)
}

export interface MapDataInput {
  [uf: string]: number
}

export interface MapBrazilProps {
  data: MapDataInput | Map<string, number>
  onStateClick?: (uf: string) => void
  isVolumeMode?: boolean
  unitLabel?: string
  className?: string
  selectedUf?: string | null
  filters?: any
}

// Configuração de Viewport e enquadramento visual calibrado por estado
export interface StateViewportConfig {
  center: [number, number]
  zoom: number
  lonOffsetDesktop: number
  latOffsetDesktop?: number
}

export const UF_STATE_VIEWPORT: Record<string, StateViewportConfig> = {
  // Norte
  PA: { center: [-52.5, -4.0], zoom: 2.4, lonOffsetDesktop: 7.8 },
  AM: { center: [-64.5, -4.0], zoom: 1.85, lonOffsetDesktop: 9.5 },
  AP: { center: [-51.8, 1.4], zoom: 3.4, lonOffsetDesktop: 5.6 },
  TO: { center: [-48.3, -10.2], zoom: 2.8, lonOffsetDesktop: 6.8 },
  RO: { center: [-62.8, -11.0], zoom: 3.2, lonOffsetDesktop: 5.9 },
  RR: { center: [-61.3, 2.0], zoom: 3.2, lonOffsetDesktop: 5.9 },
  AC: { center: [-70.5, -9.2], zoom: 3.4, lonOffsetDesktop: 5.6 },

  // Nordeste
  MA: { center: [-45.2, -5.2], zoom: 2.8, lonOffsetDesktop: 6.8 },
  PI: { center: [-42.8, -7.5], zoom: 2.9, lonOffsetDesktop: 6.5 },
  CE: { center: [-39.5, -5.2], zoom: 3.6, lonOffsetDesktop: 5.3 },
  RN: { center: [-36.5, -5.8], zoom: 4.5, lonOffsetDesktop: 4.2 },
  PB: { center: [-36.8, -7.1], zoom: 4.5, lonOffsetDesktop: 4.2 },
  PE: { center: [-37.5, -8.3], zoom: 3.8, lonOffsetDesktop: 5.0 },
  AL: { center: [-36.6, -9.6], zoom: 5.0, lonOffsetDesktop: 3.8 },
  SE: { center: [-37.4, -10.6], zoom: 5.2, lonOffsetDesktop: 3.6 },
  BA: { center: [-41.5, -12.5], zoom: 2.5, lonOffsetDesktop: 7.5 },

  // Centro-Oeste
  MT: { center: [-55.8, -13.0], zoom: 2.4, lonOffsetDesktop: 7.8 },
  MS: { center: [-54.8, -20.5], zoom: 3.0, lonOffsetDesktop: 6.3 },
  GO: { center: [-49.8, -15.9], zoom: 3.0, lonOffsetDesktop: 6.3 },
  DF: { center: [-47.88, -15.78], zoom: 6.5, lonOffsetDesktop: 2.9 },

  // Sudeste
  MG: { center: [-44.5, -18.5], zoom: 2.7, lonOffsetDesktop: 7.0 },
  ES: { center: [-40.3, -19.5], zoom: 4.5, lonOffsetDesktop: 4.2 },
  RJ: { center: [-42.5, -22.3], zoom: 4.2, lonOffsetDesktop: 4.5 },
  SP: { center: [-48.5, -22.2], zoom: 3.2, lonOffsetDesktop: 5.9 },

  // Sul
  PR: { center: [-51.3, -24.8], zoom: 3.4, lonOffsetDesktop: 5.6 },
  SC: { center: [-50.5, -27.2], zoom: 3.8, lonOffsetDesktop: 5.0 },
  RS: { center: [-52.8, -29.8], zoom: 3.0, lonOffsetDesktop: 6.3 },
}

/**
 * Calcula o centro e nível de zoom ideais para um estado focado.
 * No PC (desktop) com o painel lateral aberto, desloca o centróide para o leste,
 * posicionando o polígono do estado com perfeição na metade esquerda livre da tela.
 */
export const getStateViewport = (
  uf: string,
  isDesktop = true,
  isPanelOpen = true,
): { coordinates: [number, number]; zoom: number } => {
  const ufUpper = uf.toUpperCase()
  const cfg = UF_STATE_VIEWPORT[ufUpper]

  if (!cfg) {
    const defaultCoords = UF_COORDINATES[ufUpper] || [-54, -15]
    return {
      coordinates:
        isDesktop && isPanelOpen ? [defaultCoords[0] + 4.5, defaultCoords[1]] : defaultCoords,
      zoom: isDesktop ? 4.2 : 3.5,
    }
  }

  const lonOffset = isDesktop && isPanelOpen ? cfg.lonOffsetDesktop : 0
  const latOffset = isDesktop && isPanelOpen ? (cfg.latOffsetDesktop || 0) : 0
  const zoom = isDesktop ? cfg.zoom : Math.max(1.8, cfg.zoom * 0.85)

  return {
    coordinates: [cfg.center[0] + lonOffset, cfg.center[1] + latOffset],
    zoom,
  }
}

